import { useLobby, useSessionUser } from "~/app";
import { routeBuilder } from "~/jsx/routeBuilder";
import { Spacing } from "~/jsx/theme";
import {
  LobbyGame,
  lobbyCommandBuilder,
  lobbyValidations,
  motion,
} from "~/utils";
import { XButton, XMeapleSlotSvg, XTypography } from "../design";
import { XLobbyUser } from "./XLobbyUser";
import { getLobbyGameParams } from "~/business";

export const XLobbyGame = ({
  game,
  withOverlay = false,
}: {
  game: LobbyGame;
  withOverlay?: boolean;
}) => {
  const sessionUser = useSessionUser();
  const sessionId = sessionUser.id;
  const { update, state } = useLobby();
  const { settings, users, id: gameId } = game;
  const { maxPlayerCount } = settings;
  const { useIxExtension = false } = getLobbyGameParams(game) ?? {};

  const isInGame = LobbyGame.isInGame(game, sessionId);
  const isAdmin = LobbyGame.isAdmin(game, sessionId);
  const validations = lobbyValidations(state, sessionUser);

  const onLeave = !validations.leave(gameId)
    ? () => {
        const command = lobbyCommandBuilder("leave")(game.id);
        update(command);
      }
    : undefined;

  const onJoin = !validations.join(gameId)
    ? () => {
        const command = lobbyCommandBuilder("join")(game.id);
        update(command);
      }
    : undefined;

  const onOpen = LobbyGame.isOpenable(game, sessionId)
    ? () => {
        window.open(routeBuilder.game(game), "_blank");
      }
    : undefined;

  const onLaunch = !validations.start(gameId)
    ? () => {
        const command = lobbyCommandBuilder("start")(game.id);
        update(command);
      }
    : undefined;

  const actions = {
    join: { action: onJoin, label: "Join" },
    open: { action: onOpen, label: "Play" },
    launch: { action: onLaunch, label: "Launch" },
  };
  const nextAction = isInGame
    ? !game.started && isAdmin
      ? actions.launch
      : actions.open
    : actions.join;

  return (
    <motion.div
      layoutId={game.id}
      style={{
        ...(withOverlay ? { backgroundColor: "rgba(255, 255, 255, 0.1)" } : {}),
        padding: Spacing.px12,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div
          style={{ display: "flex", gap: Spacing.px24, alignItems: "center" }}
        >
          <div style={{ width: "fit-content" }}>
            {
              <XButton variant="action" onClick={nextAction.action}>
                {nextAction.label}
              </XButton>
            }
          </div>
          {useIxExtension && (
            <XTypography typographyStyle={{ verticalAlign: "middle" }}>
              (IX)
            </XTypography>
          )}
          <div
            style={{ display: "flex", gap: Spacing.px24, alignItems: "center" }}
          >
            {[...new Array(maxPlayerCount)].map((_, index) => {
              const user = users.at(index);
              return user ? (
                <XLobbyUser key={index} user={user} />
              ) : (
                <XTypography key={index}>
                  <XMeapleSlotSvg
                    width={24}
                    style={{
                      verticalAlign: "middle",
                      marginRight: Spacing.px4,
                    }}
                  />
                  empty
                </XTypography>
              );
            })}
          </div>
        </div>
        {onLeave && (
          <XButton variant={["action", "secondary"]} onClick={onLeave}>
            Leave
          </XButton>
        )}
      </div>
    </motion.div>
  );
};
